/**
 * The self-grading page's ticked rubric items, mirrored to `sessionStorage`
 * under `mf.exam.grade.<sessionId>` so a reload (or an accidental back) while
 * grading seven problems does not lose them. The ticks are a client-side draft
 * until "Klar med rättning" posts them, so the page clears the draft on a
 * successful submit and sign-out clears every draft (`clearAllGradeDrafts`).
 *
 * Every access is guarded: without storage (private mode, a full quota) the
 * page loses the convenience, never its state.
 */
export type GradeDraft = Record<string, string[]>;

const PREFIX = "mf.exam.grade.";

export function saveGradeDraft(sessionId: string, draft: GradeDraft): void {
  try {
    window.sessionStorage.setItem(`${PREFIX}${sessionId}`, JSON.stringify(draft));
  } catch {
    /* storage unavailable — the page still works from React state */
  }
}

export function loadGradeDraft(sessionId: string): GradeDraft | null {
  let parsed: unknown;
  try {
    const raw = window.sessionStorage.getItem(`${PREFIX}${sessionId}`);
    if (!raw) return null;
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const draft: GradeDraft = {};
  for (const [questionId, list] of Object.entries(parsed)) {
    if (!Array.isArray(list)) continue;
    draft[questionId] = list.filter((id): id is string => typeof id === "string");
  }
  return draft;
}

export function clearGradeDraft(sessionId: string): void {
  try {
    window.sessionStorage.removeItem(`${PREFIX}${sessionId}`);
  } catch {
    /* ignore */
  }
}

/** Sign-out / account deletion: no draft may outlive the signed-in user on this tab. */
export function clearAllGradeDrafts(): void {
  try {
    const storage = window.sessionStorage;
    for (let i = storage.length - 1; i >= 0; i--) {
      const name = storage.key(i);
      if (name?.startsWith(PREFIX)) storage.removeItem(name);
    }
  } catch {
    /* ignore */
  }
}
